import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiFetch } from '../lib/api';
import { SectionLabel, PanelCard } from '../components/primitives';

/**
 * SWMS task library.
 *
 * Merges the legacy "Swms Tasks" and "Swms Ranking" screens. The second had a
 * 303-line controller and no table behind it — the ranking is an attribute of a
 * task, which is how it is modelled now.
 *
 * The two legacy scales disagree on 14 of 20 tasks. That is not called out at
 * the top of the screen any more — the level shown is the one from the label,
 * which is what prints on the SWMS — but a disputed task still carries a small
 * warning next to its name, and saving it settles the dispute.
 */

interface SwmsTask {
    readonly id: number;
    readonly task: string;
    readonly hazardsRisks: string | null;
    readonly controlMeasures: string | null;
    readonly riskLevel: number | null;
    readonly riskLabel: string | null;
    readonly legacyRiskRanking: number | null;
    readonly riskConflict: boolean;
    readonly isActive: boolean;
    readonly jobCount: number;
    readonly activeJobCount: number;
}

const RISK = [
    { level: 1, label: 'Major', colour: 'error' as const },
    { level: 2, label: 'Moderate', colour: 'warning' as const },
    { level: 3, label: 'Minor', colour: 'success' as const },
];

function riskOf(level: number | null): (typeof RISK)[number] | undefined {
    return RISK.find((r) => r.level === level);
}

export function Swms(): React.JSX.Element {
    const [tasks, setTasks] = useState<readonly SwmsTask[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);
    const [editing, setEditing] = useState<SwmsTask | 'new' | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ tasks: readonly SwmsTask[] }>('/swms')
            .then((r) => {
                if (!cancelled) {
                    setTasks(r.tasks);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load SWMS tasks');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    function remove(t: SwmsTask): void {
        apiFetch<unknown>(`/swms/${String(t.id)}`, { method: 'DELETE' })
            .then(reload)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not remove');
            });
    }

    return (
        <Container maxWidth="lg" sx={{ pt: 2.5, pb: 6 }}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel divider={false}>SWMS</SectionLabel>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        The task library jobs draw their safe work method statements from.
                    </Typography>
                </Box>
                {loading ? <CircularProgress size={18} /> : null}
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditing('new');
                    }}
                >
                    New task
                </Button>
            </Stack>

            {error !== null ? (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setError(null);
                    }}
                >
                    {error}
                </Alert>
            ) : null}

            <Stack gap={1}>
                {tasks.map((t) => {
                    const risk = riskOf(t.riskLevel);
                    return (
                        <PanelCard key={t.id}>
                            <Stack direction="row" alignItems="flex-start" gap={1.5}>
                                <Box sx={{ flex: '0 0 auto', width: 96 }}>
                                    {risk === undefined ? (
                                        <Chip size="small" variant="outlined" label="No risk set" sx={{ height: 21 }} />
                                    ) : (
                                        <Chip
                                            size="small"
                                            color={risk.colour}
                                            label={risk.label}
                                            sx={{ height: 21, fontWeight: 700 }}
                                        />
                                    )}
                                    {t.riskConflict ? (
                                        <Tooltip
                                            title={`The old ranking said ${String(t.legacyRiskRanking)}, the label says ${String(t.riskLevel)}. Edit the task to settle it.`}
                                        >
                                            <Stack direction="row" alignItems="center" gap={0.25} sx={{ mt: 0.5 }}>
                                                <WarningAmberIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                                                <Typography sx={{ fontSize: '0.62rem', color: 'warning.main' }}>
                                                    disputed
                                                </Typography>
                                            </Stack>
                                        </Tooltip>
                                    ) : null}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0, opacity: t.isActive ? 1 : 0.5 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>{t.task}</Typography>
                                    {t.hazardsRisks !== null ? (
                                        <Typography
                                            sx={{
                                                fontSize: '0.78rem',
                                                color: 'text.secondary',
                                                mt: 0.5,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {t.hazardsRisks}
                                        </Typography>
                                    ) : null}
                                    {t.controlMeasures !== null ? (
                                        <Typography
                                            sx={{
                                                fontSize: '0.76rem',
                                                color: 'text.disabled',
                                                mt: 0.75,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {t.controlMeasures}
                                        </Typography>
                                    ) : null}
                                </Box>

                                <Stack alignItems="flex-end" sx={{ flex: '0 0 auto' }}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                                        {t.jobCount === 0 ? 'No jobs' : `${String(t.jobCount)} jobs`}
                                    </Typography>
                                    <Stack direction="row" alignItems="center">
                                        <Tooltip title={t.isActive ? 'In the library' : 'Hidden from jobs'}>
                                            <Switch
                                                size="small"
                                                checked={t.isActive}
                                                onChange={(e) => {
                                                    apiFetch<unknown>(`/swms/${String(t.id)}`, {
                                                        method: 'PATCH',
                                                        body: JSON.stringify({ isActive: e.target.checked }),
                                                    })
                                                        .then(reload)
                                                        .catch((err: unknown) => {
                                                            setError(
                                                                err instanceof Error ? err.message : 'Could not save',
                                                            );
                                                        });
                                                }}
                                            />
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setEditing(t);
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <Tooltip
                                            title={t.jobCount > 0 ? 'Attached to jobs — turn it off instead' : 'Remove'}
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={t.jobCount > 0}
                                                    onClick={() => {
                                                        remove(t);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </PanelCard>
                    );
                })}
            </Stack>

            {tasks.length === 0 && !loading ? (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.disabled', fontSize: '0.85rem' }}>
                    No SWMS tasks yet.
                </Typography>
            ) : null}

            <SwmsDialog
                open={editing !== null}
                task={editing === 'new' ? null : editing}
                onClose={() => {
                    setEditing(null);
                }}
                onSaved={() => {
                    setEditing(null);
                    reload();
                }}
            />
        </Container>
    );
}

// ─── Dialog ─────────────────────────────────────────────────────────────────

interface SwmsDialogProps {
    readonly open: boolean;
    readonly task: SwmsTask | null;
    readonly onClose: () => void;
    readonly onSaved: () => void;
}

function SwmsDialog({ open, task, onClose, onSaved }: SwmsDialogProps): React.JSX.Element {
    const [name, setName] = useState('');
    const [hazards, setHazards] = useState('');
    const [controls, setControls] = useState('');
    const [riskLevel, setRiskLevel] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError(null);
        setName(task?.task ?? '');
        setHazards(task?.hazardsRisks ?? '');
        setControls(task?.controlMeasures ?? '');
        setRiskLevel(task?.riskLevel === null || task === null ? '' : String(task.riskLevel));
    }, [open, task]);

    function save(): void {
        setSaving(true);
        setError(null);
        const body = {
            task: name.trim(),
            hazardsRisks: hazards,
            controlMeasures: controls,
            riskLevel: riskLevel === '' ? null : Number(riskLevel),
        };
        const path = task === null ? '/swms' : `/swms/${String(task.id)}`;
        apiFetch<unknown>(path, { method: task === null ? 'POST' : 'PATCH', body: JSON.stringify(body) })
            .then(onSaved)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>{task === null ? 'New SWMS task' : 'Edit SWMS task'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Task"
                        size="small"
                        required
                        autoFocus
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                        }}
                    />
                    <TextField
                        select
                        label="Risk level"
                        size="small"
                        value={riskLevel}
                        onChange={(e) => {
                            setRiskLevel(e.target.value);
                        }}
                        helperText={
                            task?.riskConflict === true
                                ? `Disputed: the old ranking said ${String(task.legacyRiskRanking)}, the label said ${String(task.riskLevel)}. Saving settles it.`
                                : ' '
                        }
                    >
                        <MenuItem value="">Not set</MenuItem>
                        {RISK.map((r) => (
                            <MenuItem key={r.level} value={String(r.level)}>
                                {r.level} — {r.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Hazards and risks"
                        multiline
                        minRows={2}
                        value={hazards}
                        onChange={(e) => {
                            setHazards(e.target.value);
                        }}
                    />
                    <TextField
                        label="Control measures"
                        multiline
                        minRows={4}
                        value={controls}
                        onChange={(e) => {
                            setControls(e.target.value);
                        }}
                    />
                    {error !== null ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={save} disabled={saving || name.trim() === ''}>
                    {task === null ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
